// NIST AI Risk Management Framework (AI RMF 1.0) Readiness Assessment.
// 14 questions across the four AI RMF Functions — Govern, Map, Measure, Manage —
// each scored 1-5. The overall average maps to an indicative readiness level.
const q = (id, domain, prompt, options) => ({ id, domain, prompt, options });
const opt = (label, value) => ({ label, value });
const ladder = (l1, l2, l3, l4, l5) => [
  opt(l1, 1), opt(l2, 2), opt(l3, 3), opt(l4, 4), opt(l5, 5),
];

export const aiRmfDomains = ['Govern', 'Map', 'Measure', 'Manage'];

export const aiRmfQuestions = [
  // --- GOVERN ---
  q('gov-1', 'Govern', 'Do you have policies governing how AI is developed, procured, and used?', ladder(
    'No AI-specific policies',
    'Informal guidance only',
    'Basic AI use policy exists',
    'Documented AI governance covering development, procurement, and use',
    'Governance is board-level, resourced, and reviewed regularly',
  )),
  q('gov-2', 'Govern', 'Are roles and accountability for AI risk clearly assigned?', ladder(
    'No one owns AI risk',
    'Ownership is unclear or informal',
    'Key roles identified for AI oversight',
    'Accountability, escalation, and sign-off defined',
    'A cross-functional AI risk function operates with authority',
  )),
  q('gov-3', 'Govern', 'Do you consider trustworthy-AI characteristics (fairness, transparency, privacy, safety, security)?', ladder(
    'Not considered',
    'Considered ad hoc for some projects',
    'Considered for high-impact systems',
    'A defined set of trustworthy-AI requirements is applied',
    'Requirements are measured and enforced across the lifecycle',
  )),
  q('gov-4', 'Govern', 'Do you maintain an inventory of AI systems and their risk classification?', ladder(
    'No inventory of AI systems',
    'A partial or informal list',
    'Inventory of production AI systems',
    'Inventory with risk tiering and ownership',
    'Continuously maintained inventory tied to controls and reviews',
  )),

  // --- MAP ---
  q('map-1', 'Map', 'Do you assess the context, purpose, and potential impacts of an AI system before deployment?', ladder(
    'No impact assessment',
    'Considered informally',
    'Impact assessed for major systems',
    'Structured impact/context assessment is standard',
    'Assessment includes affected people and is revisited over time',
  )),
  q('map-2', 'Map', 'Do you identify risks and potential harms (to individuals, groups, and the organization)?', ladder(
    'Harms are not identified',
    'Only obvious risks are noted',
    'Risks documented for key systems',
    'Systematic risk identification with stakeholders',
    'Harms mapped continuously, including downstream and misuse cases',
  )),
  q('map-3', 'Map', 'Do you document data provenance, quality, and limitations for AI systems?', ladder(
    'Not documented',
    'Partially, for some datasets',
    'Documented for production models',
    'Provenance, quality, and known limitations recorded',
    'Continuously tracked with lineage and drift awareness',
  )),
  q('map-4', 'Map', 'Do you evaluate third-party / vendor AI and foundation models for risk?', ladder(
    'No evaluation of third-party AI',
    'We rely on the vendor’s word',
    'Basic due diligence at procurement',
    'Risk-based evaluation of third-party AI and models',
    'Ongoing monitoring of third-party AI with contractual terms',
  )),

  // --- MEASURE ---
  q('meas-1', 'Measure', 'Do you test AI systems for performance, robustness, bias, and security before and after release?', ladder(
    'No testing beyond basic accuracy',
    'Ad hoc testing on some systems',
    'Pre-release testing for key metrics',
    'Structured testing incl. bias, robustness, and security',
    'Continuous evaluation with red-teaming and adversarial testing',
  )),
  q('meas-2', 'Measure', 'Do you monitor AI systems in production for drift, degradation, and misuse?', ladder(
    'No production monitoring',
    'Monitored only if something breaks',
    'Basic performance monitoring',
    'Monitoring for drift, quality, and abuse',
    'Continuous monitoring with alerting and defined thresholds',
  )),
  q('meas-3', 'Measure', 'Are metrics and evidence for AI trustworthiness documented and reviewed?', ladder(
    'No metrics captured',
    'Metrics captured inconsistently',
    'Key metrics recorded for major systems',
    'Metrics reviewed against defined criteria',
    'Metrics feed governance decisions and are independently reviewed',
  )),

  // --- MANAGE ---
  q('man-1', 'Manage', 'Do you prioritize and treat identified AI risks (mitigate, transfer, avoid, accept)?', ladder(
    'Risks are not actively managed',
    'Handled reactively',
    'Priority risks are treated for key systems',
    'Risks are prioritized and treatment tracked to closure',
    'Risk treatment is continuous and tied to risk appetite',
  )),
  q('man-2', 'Manage', 'Do you have incident response and recovery plans specific to AI failures or misuse?', ladder(
    'No AI-specific response plan',
    'General IT plan only',
    'Basic plan for major AI systems',
    'AI incident playbooks with defined roles',
    'Tested AI incident response with post-incident learning',
  )),
  q('man-3', 'Manage', 'Can you decommission, roll back, or override an AI system that behaves unacceptably?', ladder(
    'No defined off-switch or rollback',
    'Possible but slow and manual',
    'Rollback exists for key systems',
    'Defined override/rollback with human oversight',
    'Rehearsed kill-switch and human-in-the-loop controls',
  )),
];

export const aiRmfMeta = {
  eyebrow: 'Free Tool · NIST AI RMF',
  heroTitle: 'NIST AI RMF Readiness Assessment',
  heroDesc:
    'Fourteen questions across the four functions of the NIST AI Risk Management Framework — Govern, Map, Measure, and Manage. About five minutes, and you get an indicative readiness level for managing AI risk responsibly, plus where to focus first.',
  resultsIntro: 'Your Indicative AI RMF Readiness',
  domainLabel: 'AI RMF Function',
  frameworkShort: 'AI governance program',
  source: 'NIST AI RMF Assessment',
  disclaimer:
    'This readiness level is indicative only — it is estimated from the answers you provided in this short self-assessment, not from a review of your AI systems, data, or evidence. It is not a formal NIST AI RMF assessment, audit, or certification, and a hands-on evaluation by our team may reach a different conclusion. Treat it as a starting point for the conversation, not a final result.',
};

const aiTier = (avg) => {
  if (avg < 1.75) return {
    tier: 'Emerging',
    desc: 'AI is being used or built without a defined risk-management approach. The priority is to establish basic governance — an AI inventory, ownership, and a use policy — before scaling AI further.',
  };
  if (avg < 2.75) return {
    tier: 'Developing',
    desc: 'Some AI risk practices exist but are inconsistent across teams and systems. Formalizing how you map and measure AI risk will have the biggest impact at this stage.',
  };
  if (avg < 3.75) return {
    tier: 'Established',
    desc: 'You have repeatable governance, risk mapping, and testing for AI. The next gain comes from continuous measurement in production and rehearsing how you manage AI incidents.',
  };
  return {
    tier: 'Leading',
    desc: 'AI risk is governed, measured, and managed continuously, with trustworthy-AI requirements embedded across the lifecycle. Focus shifts to adversarial testing, independent assurance, and staying ahead of regulation.',
  };
};

export const computeAiRmfResult = (answers, questions) => {
  const byDomain = {};
  questions.forEach((ques) => {
    const val = answers[ques.id] ?? 1;
    (byDomain[ques.domain] = byDomain[ques.domain] || []).push(val);
  });
  const domainScores = Object.entries(byDomain).map(([domain, vals]) => ({
    domain,
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
  }));
  const overallAvg = domainScores.reduce((s, d) => s + d.avg, 0) / domainScores.length;
  const { tier, desc } = aiTier(overallAvg);
  const weakest = [...domainScores].sort((a, b) => a.avg - b.avg)[0];

  return {
    overallAvg,
    overallPct: Math.round((overallAvg / 5) * 100),
    tier,
    tierDesc: desc,
    domainScores,
    weakestDomain: weakest.domain,
    ctaHref: '/services/nist-ai-rmf',
    ctaLabel: 'See NIST AI RMF Services',
  };
};
